import React, { useMemo, useState } from 'react';
import { MIDDLE_STYLE } from '@constants/responsive';
import { GLOBAL_THEME_COLOR } from '@constants/theme';
import { FacebookPostSizeMode, InstagramPostSizeMode, Review, SocialPlatform } from '@core/models';
import { getSocialPostSizeMode } from '@utils/social.util';
import { Button, Rate, Select, Space } from 'antd';
import { useAuthState } from '@core/configureStore';
import { shortenString } from '@utils/string.util';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';

type Props = {
  review: Review;
  onClose: () => void;
};

const ReviewSocialPostEditor = ({ review, onClose }: Props) => {
  const { user } = useAuthState();
  const printRef = React.useRef(null);
  // const [caption, setCaption] = useState<string>('');
  const [platform, setPlatofrm] = useState<SocialPlatform>(SocialPlatform.Facebook);
  const [sizeMode, setSizeMode] = useState<FacebookPostSizeMode>(FacebookPostSizeMode.Square);
  const [imageWidth, imageHeight] = useMemo(() => getSocialPostSizeMode(sizeMode), [sizeMode]);

  const handleCancel = () => {
    onClose();
  };

  const handleDownloadImage = async () => {
    const element = printRef.current;
    if (!element) return;
    const canvas = await html2canvas(element, {
      allowTaint: true,
      useCORS: true,
    });

    const data = canvas.toDataURL('image/png');
    const link = document.createElement('a');

    if (typeof link.download === 'string') {
      link.href = data;
      link.download = `${user?.selected_location.title}__${review.reviewer.displayName}.jpg`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  // useEffect(() => {
  //   const init = async () => {
  //     setCaption(
  //       `We are thrilled to showcase a prestigious ${review.rating}-star review from ${review.reviewer.displayName}, emphasizing the exceptional standards upheld at ${user?.selected_location.title}. With utmost gratitude, we extend our appreciation for their kind words.`
  //     );
  //   };
  //   init();
  // }, [review, user]);

  return (
    <div style={{ padding: '10px 0px' }}>
      <div
        style={{
          ...MIDDLE_STYLE,
          justifyContent: 'space-between',
          padding: '10px 15px',
          backgroundColor: GLOBAL_THEME_COLOR.$primary_color,
          border: `1px solid ${GLOBAL_THEME_COLOR.$border_color}`,
          borderRadius: 10,
          boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
        }}>
        <Space direction="horizontal">
          <Select
            style={{ width: 'fit-content' }}
            value={platform}
            onChange={value => setPlatofrm(value)}
            options={Object.keys(SocialPlatform).map(key => ({
              label: key,
              value: key,
            }))}
          />
          <Select
            style={{ width: 'fit-content' }}
            value={sizeMode}
            onChange={value => setSizeMode(value)}
            options={Object.keys(
              platform === SocialPlatform.Facebook ? FacebookPostSizeMode : InstagramPostSizeMode
            ).map(key => ({
              label: key,
              value: key,
            }))}
          />
        </Space>
        <Space direction="horizontal">
          <Button type="primary" onClick={handleDownloadImage}>
            <DownloadOutlined /> Download Image
          </Button>
          <Button onClick={handleCancel} style={{ color: GLOBAL_THEME_COLOR.$error_color }}>
            <CloseOutlined /> Cancel
          </Button>
        </Space>
      </div>
      <div style={{ ...MIDDLE_STYLE }}>
        <div
          style={{
            ...MIDDLE_STYLE,
            backgroundColor: GLOBAL_THEME_COLOR.$primary_color,
            width: '80%',
            flexDirection: 'column',
            marginTop: 20,
            boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
          }}>
          <div
            ref={printRef}
            style={{
              backgroundColor: GLOBAL_THEME_COLOR.$dark_highlight_color,
              width: '100%',
              aspectRatio: imageWidth / imageHeight,
              overflow: 'hidden',
              ...MIDDLE_STYLE,
              flexDirection: 'column',
              padding: '20px 30px',
            }}>
            {sizeMode === FacebookPostSizeMode.Square && (
              <div style={{ textAlign: 'center', marginBottom: 30 }}>
                <p style={{ color: GLOBAL_THEME_COLOR.$text_color_contrast }}>Best review from</p>
                <h1 style={{ color: GLOBAL_THEME_COLOR.$text_color_contrast }}>
                  {user?.selected_location.title}
                </h1>
                <p style={{ color: GLOBAL_THEME_COLOR.$text_color_contrast }}>
                  {user?.selected_location.full_address}
                </p>
              </div>
            )}
            <div
              style={{
                ...MIDDLE_STYLE,
                flexDirection: 'column',
                width: '100%',
                backgroundColor: GLOBAL_THEME_COLOR.$primary_color,
                boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                padding: '30px 30px',
                borderRadius: 20,
                transform: 'rotate(-2deg)',
              }}>
              <Rate style={{ fontSize: 30 }} value={review.rating} disabled />
              <p
                style={{
                  marginTop: 20,
                  fontSize: 15,
                  fontWeight: 'bold',
                }}>
                <span>"</span>
                {shortenString(
                  review.comment || 'N/A',
                  sizeMode === FacebookPostSizeMode.Landscape ? 300 : 500
                )}
                <span>"</span>
              </p>
              <h2 style={{ fontSize: 14, float: 'right', marginTop: 20 }}>
                - {review.reviewer.displayName} -
              </h2>
            </div>
          </div>
          {/* <div style={{ padding: '10px 10px', width: '100%' }}>
            <Input.TextArea
              bordered={false}
              style={{
                backgroundColor: GLOBAL_THEME_COLOR.$primary_color,
                width: '100%',
                fontWeight: 'normal',
                color: 'black',
              }}
              onChange={e => setCaption(e.target.value)}
              placeholder="Enter a post caption (optional)"
              value={caption}
              autoSize
              allowClear
            />
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default ReviewSocialPostEditor;
